import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { SkillPackageFormState } from "../utils/skill-package-draft"

import {
  MAX_SKILL_DRAFT_BRIEF_LENGTH,
  MIN_SKILL_DRAFT_BRIEF_LENGTH,
} from "@/api/admin/skill-packages"
import { Sparkles, TriangleAlert } from "@/components/icons"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

import { useDraftSkillPackageMutation } from "../hooks/queries/skill-packages"
import { applyGeneratedDraft } from "../utils/skill-package-draft"
import { describeSkillPackageError } from "../utils/skill-packages"

// The draft assistant (#3074).
//
// The administrator describes the skill in their own words; the backend turns
// that into the format's parts. Everything about this component is built
// around one rule from the issue: the result fills an EDITABLE draft and
// nothing is ever published by it.
//
// That rule shows up three ways in the markup, not just in the code:
//
// * the panel is labelled a draft generator, in a badge that is part of the
//   heading rather than fine print under the button;
// * the button says "fill the draft", never "create" or "publish" — the only
//   thing it changes is unsaved form state;
// * a refusal says what the model did wrong (the backend validated its output
//   against the package model first) and leaves the form exactly as it was.
//
// The prompt is NOT here. It is platform behaviour and lives in
// `services/skills/package_drafting.py`; this component sends only what the
// administrator typed.

export function SkillPackageAiAssistant({
  form,
  onDraft,
  disabled,
}: {
  form: SkillPackageFormState
  onDraft: (next: SkillPackageFormState) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const [brief, setBrief] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [droppedTools, setDroppedTools] = useState<string[]>([])
  const draftMutation = useDraftSkillPackageMutation()

  const trimmed = brief.trim()
  const tooShort = trimmed.length < MIN_SKILL_DRAFT_BRIEF_LENGTH
  const isPublished = form.latestVersion > 0

  const onGenerate = async () => {
    setError(null)
    setNotice(null)
    setDroppedTools([])
    try {
      const result = await draftMutation.mutateAsync({
        description: trimmed,
        ...(form.name.trim() ? { name: form.name.trim() } : {}),
      })
      onDraft(applyGeneratedDraft(form, result.draft))
      setDroppedTools(result.dropped_tools)
      setNotice(
        t("adminDashboard.skillPackages.assistant.filled", {
          defaultValue:
            "The draft below was filled in. Read it through and change anything you disagree with — nothing is saved until you press Save.",
        })
      )
    } catch (exc) {
      const described = describeSkillPackageError(exc, "draft")
      const text = t(described.key, { defaultValue: described.defaultValue })
      setError(
        described.detail && described.detail !== text ? `${text} (${described.detail})` : text
      )
    }
  }

  return (
    <section
      className="bg-muted/40 space-y-3 rounded-lg border p-3 sm:p-4"
      data-testid="skill-package-assistant"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles aria-hidden className="text-foreground size-4 shrink-0" />
        <h4 className="text-sm font-semibold">
          {t("adminDashboard.skillPackages.assistant.title", {
            defaultValue: "Describe the skill and let AI write a first draft",
          })}
        </h4>
        <Badge variant="secondary">
          {t("adminDashboard.skillPackages.assistant.badge", { defaultValue: "Draft generator" })}
        </Badge>
      </div>

      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.skillPackages.assistant.help", {
          defaultValue:
            "Write what the skill should do, in your own words. The assistant rewrites it into the format's parts — instructions, when the skill applies, working steps, delivery contract and suggested eval cases — and fills the editable draft below. It never publishes, and every word stays yours to change.",
        })}
      </p>

      <div className="space-y-2">
        <Label htmlFor="skill-package-brief">
          {t("adminDashboard.skillPackages.assistant.briefLabel", {
            defaultValue: "What should the skill do?",
          })}
        </Label>
        <Textarea
          data-testid="skill-package-brief"
          disabled={disabled || draftMutation.isPending}
          id="skill-package-brief"
          maxLength={MAX_SKILL_DRAFT_BRIEF_LENGTH}
          rows={3}
          value={brief}
          placeholder={t("adminDashboard.skillPackages.assistant.briefPlaceholder", {
            defaultValue:
              "e.g. analyse tender documents and deliver an HTML report where every requirement cites its source",
          })}
          onChange={(event) => setBrief(event.target.value)}
        />
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.skillPackages.assistant.briefCounter", {
            defaultValue: "{{used}} / {{max}} characters",
            used: brief.length,
            max: MAX_SKILL_DRAFT_BRIEF_LENGTH,
          })}
        </p>
      </div>

      {isPublished ? (
        <p className="text-muted-foreground text-xs">
          {t("adminDashboard.skillPackages.assistant.publishedNote", {
            defaultValue:
              "v{{version}} is published and frozen. A generated draft becomes the next version only when you save and publish it.",
            version: form.latestVersion,
          })}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          data-testid="skill-package-generate"
          disabled={disabled || tooShort || draftMutation.isPending}
          type="button"
          onClick={() => void onGenerate()}
        >
          {draftMutation.isPending ? (
            <Spinner className="size-4" />
          ) : (
            <Sparkles className="size-4" />
          )}
          {t("adminDashboard.skillPackages.assistant.generate", {
            defaultValue: "Fill the draft with AI",
          })}
        </Button>
        {tooShort && trimmed.length > 0 ? (
          <span className="text-muted-foreground text-xs">
            {t("adminDashboard.skillPackages.assistant.tooShort", {
              defaultValue: "Describe the skill in at least {{min}} characters.",
              min: MIN_SKILL_DRAFT_BRIEF_LENGTH,
            })}
          </span>
        ) : null}
      </div>

      {error ? (
        <Alert
          className="border-foreground bg-destructive text-destructive-foreground dark:border-foreground [&>svg]:text-destructive-foreground"
          data-testid="skill-package-assistant-error"
          variant="destructive"
        >
          <TriangleAlert className="size-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {notice ? (
        <p className="text-muted-foreground text-xs" data-testid="skill-package-assistant-notice">
          {notice}
        </p>
      ) : null}

      {droppedTools.length > 0 ? (
        <Alert data-testid="skill-package-dropped-tools">
          <TriangleAlert className="size-4" />
          <AlertDescription>
            {t("adminDashboard.skillPackages.assistant.droppedTools", {
              defaultValue:
                "The assistant suggested tools this platform does not have; they were left out: {{tools}}. Pick the real ones under Tools.",
              tools: droppedTools.join(", "),
            })}
          </AlertDescription>
        </Alert>
      ) : null}
    </section>
  )
}
